"use client";

import type React from "react";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useAppTheme } from "../ThemeContext.tsx";
import PasswordStrengthBar from "./PasswordStrengthBar.tsx";
import {
  validatePassword,
  PASSWORD_MIN_LENGTH,
} from "../lib/passwordPolicy.ts";

const Signup = () => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const outerBg = isDark ? "gray.800" : "gray.100";
  const innerBg = isDark ? "gray.700" : "white";
  const headingColor = isDark ? "white" : "black";
  const textColor = isDark ? "gray.300" : "gray.600";
  const inputBg = isDark ? "gray.600" : "gray.200";
  const linkColor = isDark ? "gray.300" : "gray.500";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const dashboardIconUrl = process.env.REACT_APP_MAIN_LOGO_URL || "";

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      toast({
        title: "Passwords don't match",
        description: "Please make sure your passwords match.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    const passwordCheck = validatePassword(password);
    if (!passwordCheck.valid) {
      const message =
        passwordCheck.message || "Password does not meet requirements";
      setError(message);
      toast({
        title: "Password requirements not met",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          password,
          firstName,
          lastName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          const message =
            data.message ||
            "This email is already registered. Please sign in instead.";
          toast({
            title: "Email already registered",
            description: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          setError(message);
        } else {
          const errorMessage =
            data.error ||
            data.message ||
            "An error occurred during signup. Please try again.";
          toast({
            title: "Signup failed",
            description: errorMessage,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          setError(errorMessage);
        }
        return;
      }

      toast({
        title: "Account created successfully",
        description:
          data.message ||
          "Please check your email to verify your account.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      navigate("/otp-verification", {
        state: {
          email: email.toLowerCase().trim(),
        },
      });
    } catch {
      const message = "An unexpected error occurred. Please try again.";
      toast({
        title: "Unexpected error",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={outerBg}
    >
      <Box
        width="full"
        maxWidth="md"
        p={8}
        borderRadius="md"
        bg={innerBg}
        boxShadow="lg"
      >
        <VStack spacing={6}>
          {dashboardIconUrl && (
            <img
              src={dashboardIconUrl || "/placeholder.svg"}
              alt="Logo"
              style={{ maxHeight: "60px", marginBottom: "1rem" }}
            />
          )}
          <Heading color={headingColor}>Create an account</Heading>
          <Text color={textColor}>Sign up to get started</Text>

          {error && (
            <Alert status="error" borderRadius="md" width="100%">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <form onSubmit={handleSignup} style={{ width: "100%" }}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel color={textColor}>First Name</FormLabel>
                <Input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  isDisabled={loading}
                  bg={inputBg}
                  color={headingColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Last Name</FormLabel>
                <Input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  isDisabled={loading}
                  bg={inputBg}
                  color={headingColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Email</FormLabel>
                <Input
                  id="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  isDisabled={loading}
                  bg={inputBg}
                  color={headingColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Password</FormLabel>
                <InputGroup>
                  <Input
                    id="password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    isDisabled={loading}
                    bg={inputBg}
                    color={headingColor}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                      isDisabled={loading}
                      variant="ghost"
                      colorScheme={isDark ? "white" : "black"}
                    />
                  </InputRightElement>
                </InputGroup>
                <PasswordStrengthBar password={password} />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Confirm Password</FormLabel>
                <InputGroup>
                  <Input
                    id="confirm-password-input"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    isDisabled={loading}
                    bg={inputBg}
                    color={headingColor}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                      icon={
                        showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />
                      }
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      isDisabled={loading}
                      variant="ghost"
                      colorScheme={isDark ? "white" : "black"}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <Button
                type="submit"
                colorScheme="gray"
                _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                background={isDark ? "gray.700" : "gray.200"}
                textColor={isDark ? "whiteAlpha" : "gray.800"}
                width="full"
                isLoading={loading}
                loadingText="Creating account..."
              >
                Create account
              </Button>
            </VStack>
          </form>
          <Text color={textColor}>
            Already have an account?{" "}
            <Link
              to="/login"
              style={{ color: linkColor, textDecoration: "underline" }}
            >
              Sign in
            </Link>
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default Signup;
